import { Tables } from '../../db_types'

export type MetricReference = Pick<
  Tables<'metrics'>,
  'id' | 'label' | 'source' | 'formula' | 'organization_id' | 'archived_at' | 'user_field' | 'type'
>

export function getMetricReferenceIds(formula: string | null | undefined) {
  if (!formula) return []

  return Array.from(formula.matchAll(/metric_(\d+)/g))
    .map(match => Number(match[1]))
    .filter(metricId => !Number.isNaN(metricId))
}

export async function resolveMetricDependencyGraph(
  rootMetricIds: number[],
  loadMetrics: (metricIds: number[]) => Promise<MetricReference[]>
) {
  const metricsById = new Map<number, MetricReference>()
  const pendingMetricIds = [...new Set(rootMetricIds)]
  const missingMetricIds = new Set<number>()

  while (pendingMetricIds.length > 0) {
    const requestedMetricIds = Array.from(
      new Set(pendingMetricIds.splice(0).filter(metricId => !metricsById.has(metricId)))
    )

    if (requestedMetricIds.length === 0) continue

    const metrics = await loadMetrics(requestedMetricIds)
    const loadedMetricIds = new Set(metrics.map(metric => metric.id))

    requestedMetricIds.forEach(metricId => {
      if (!loadedMetricIds.has(metricId)) missingMetricIds.add(metricId)
    })

    metrics.forEach(metric => metricsById.set(metric.id, metric))
    pendingMetricIds.push(
      ...metrics.flatMap(metric => (metric.source ? [] : getMetricReferenceIds(metric.formula)))
    )
  }

  const reachableMetricIds = new Set<number>()
  const visitingMetricIds = new Set<number>()
  let hasCycle = false

  const visit = (metricId: number) => {
    if (visitingMetricIds.has(metricId)) {
      hasCycle = true
      return
    }
    if (reachableMetricIds.has(metricId)) return

    visitingMetricIds.add(metricId)
    reachableMetricIds.add(metricId)
    const metric = metricsById.get(metricId)
    if (!metric?.source) {
      getMetricReferenceIds(metric?.formula).forEach(visit)
    }
    visitingMetricIds.delete(metricId)
  }

  rootMetricIds.forEach(visit)

  return { metricsById, reachableMetricIds, missingMetricIds, hasCycle }
}
